import { DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  LOCALE_ID,
  OnInit,
  output,
  signal,
  viewChild,
  ViewChild,
} from '@angular/core';
import {
  ModalComponent,
  ModalWrapperComponent,
  SpinnerComponent,
} from '@catch/general-ui';
import { TranslocoPipe } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import { StripeEmbeddedCheckout } from '@stripe/stripe-js';
import { firstValueFrom } from 'rxjs';
import { StripeAdapter } from '../../adapters/stripe.adapter';
import { CompanyUserRole } from '../../enums/company-user.role.enum';
import {
  CreditsBookingModalCloseResult,
  FirstBookingPaymentMethod,
} from '../../enums/stripe.enum';
import { StripeProduct } from '../../interfaces/stripe';
import { BookingConfirmationService } from '../../services/booking-confirmation.service';
import { StripeService } from '../../services/stripe.service';
import { hasPermission } from '../../store/auth/auth.reducer';
import { selectTotalLeftCredits } from '../../store/credits/credits.reducer';
import {
  CUSTOM_CREDITS_OPTION,
  CreditsPackageSelectorComponent,
} from '../booking-selectors/credits-package-selector/credits-package-selector.component';
import { ButtonComponent } from '../button/button.component';
import { CreditsAutoRenewalSetupComponent } from '../credits-auto-renewal-setup/credits-auto-renewal-setup.component';
import { InvoiceBookingConfirmModalComponent } from '../invoice-booking-confirm-modal/invoice-booking-confirm-modal.component';
import { PaymentMethodChoiceComponent } from '../payment-method-choice/payment-method-choice.component';

export interface CreditsBookingData {
  amount: number;
  leftCredits?: number;
  onContinueWithCredits?: () => void;
  openCheckout?: boolean;
  budgetExceeded?: boolean;
}

export { CreditsBookingModalCloseResult };

@Component({
  selector: 'app-credits-booking-modal',
  imports: [
    TranslocoPipe,
    ButtonComponent,
    ModalComponent,
    SpinnerComponent,
    InvoiceBookingConfirmModalComponent,
    CreditsPackageSelectorComponent,
    CreditsAutoRenewalSetupComponent,
    PaymentMethodChoiceComponent,
  ],
  templateUrl: './credits-booking-modal.component.html',
  styleUrl: './credits-booking-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreditsBookingModalComponent
  extends ModalWrapperComponent
  implements OnInit
{
  readonly modalResult = output<CreditsBookingModalCloseResult | undefined>();

  private readonly locale = inject(LOCALE_ID);
  private readonly decimalPipe = new DecimalPipe(this.locale);
  stripeAdapter = inject(StripeAdapter);
  stripeService = inject(StripeService);
  private readonly bookingConfirmation = inject(BookingConfirmationService);
  checkoutLoading = signal<boolean>(false);
  checkout: StripeEmbeddedCheckout | undefined;
  showCheckout = signal<boolean>(false);
  isPaymentMethodStep = signal(false);
  isAutoRenewalStep = signal(false);
  choiceBusy = signal(false);
  isConfirmationMode = signal<boolean>(false);
  store = inject(Store);
  isUserAdmin = this.store.selectSignal(hasPermission(CompanyUserRole.ADMIN));
  totalLeftCredits = this.store.selectSignal(selectTotalLeftCredits);
  creditsPackages = signal<StripeProduct[]>([]);
  creditsPackagesLoaded = signal(false);
  selectedOption = signal<string | null>(null);
  customPackageCount = signal(1);
  budgetExceeded = signal(false);

  readonly CUSTOM_CREDITS_OPTION = CUSTOM_CREDITS_OPTION;

  /** Smallest credits package, bookable multiple times via the stepper. */
  readonly smallestPackage = computed(
    () =>
      [...this.creditsPackages()].sort(
        (first, second) =>
          this.creditsAmountOf(first) - this.creditsAmountOf(second),
      )[0] ?? null,
  );

  /** Catalog price behind the current selection. */
  readonly selectedPackage = computed(() => {
    const selected = this.selectedOption();
    if (!selected) {
      return null;
    }
    if (selected === CUSTOM_CREDITS_OPTION) {
      return this.smallestPackage();
    }
    return this.creditsPackages().find((pack) => pack.id === selected) ?? null;
  });

  /** How often the selected package is booked. */
  readonly selectedQuantity = computed(() =>
    this.selectedOption() === CUSTOM_CREDITS_OPTION
      ? Math.max(1, this.customPackageCount())
      : 1,
  );

  /**
   * Whether the selected credits package can be booked.
   * @returns {boolean}
   */
  readonly canBook = computed(
    () =>
      this.isUserAdmin() &&
      !!this.selectedPackage() &&
      !this.checkoutLoading() &&
      !this.showCheckout() &&
      !this.isPaymentMethodStep() &&
      !this.isAutoRenewalStep(),
  );

  /**
   * Title for the credits booking modal, including payment-method and auto-renewal steps.
   * @returns {string}
   */
  readonly modalTitleKey = computed(() => {
    if (this.isPaymentMethodStep()) {
      return 'company-tool.snippets.payment-method-choice.title';
    }
    if (this.isAutoRenewalStep()) {
      return 'company-tool.snippets.credits-booking-modal.auto-renewal.title';
    }
    return 'company-tool.snippets.credits-booking-modal.title';
  });

  private readonly bookingContext = signal<CreditsBookingData>({ amount: 0 });
  private closeResultNotified = false;
  private bookedSuccessfully = false;

  @ViewChild('confirmationModal') confirmationModal!: ModalComponent;
  @ViewChild('invoiceConfirm')
  invoiceConfirmModal!: InvoiceBookingConfirmModalComponent;
  private readonly paymentMethodChoice =
    viewChild<PaymentMethodChoiceComponent>('paymentMethodChoice');
  private readonly autoRenewalSetup =
    viewChild<CreditsAutoRenewalSetupComponent>('autoRenewalSetup');

  /**
   * Whether the first-booking auto-renewal prompt can be saved.
   * @returns {boolean}
   */
  readonly canConfirmAutoRenewal = computed(
    () => this.autoRenewalSetup()?.canSaveEnabled() ?? false,
  );

  ngOnInit(): void {
    this.loadCreditsPackages();
  }

  /**
   * Amount of credits required for the pending action.
   * @returns {number}
   */
  bookingAmount(): number {
    return this.bookingContext().amount;
  }

  /**
   * Formats the required credits for display.
   * @returns {string}
   */
  bookingAmountFormatted(): string {
    return this.formatCredits(this.bookingAmount());
  }

  /**
   * Remaining credits shown in the confirmation step.
   * @returns {number | undefined}
   */
  bookingLeftCredits(): number | undefined {
    return this.bookingContext().leftCredits;
  }

  /**
   * Formats remaining credits for the confirmation step.
   * @returns {string}
   */
  bookingLeftCreditsFormatted(): string {
    const leftCredits = this.bookingLeftCredits();
    return leftCredits == null ? '' : this.formatCredits(leftCredits);
  }

  /**
   * Reads credits_amount from price or product metadata.
   * @param {StripeProduct} pack Catalog price
   * @returns {number}
   */
  creditsAmountOf(pack: StripeProduct): number {
    const fromPrice = +(pack.metadata?.['credits_amount'] ?? 0);
    if (fromPrice > 0) return fromPrice;
    const product = pack.product as
      { metadata?: Record<string, string> } | undefined;
    return +(product?.metadata?.['credits_amount'] ?? 0);
  }

  /**
   * Applies host data before the modal opens.
   * @param {CreditsBookingData} data Booking context
   * @returns {void}
   */
  configureFromData(data: CreditsBookingData): void {
    this.teardownCheckout();
    this.checkoutLoading.set(false);
    this.isConfirmationMode.set(false);
    this.isPaymentMethodStep.set(false);
    this.isAutoRenewalStep.set(false);
    this.choiceBusy.set(false);
    this.budgetExceeded.set(!!data.budgetExceeded);
    this.showCheckout.set(false);
    this.customPackageCount.set(1);
    this.bookedSuccessfully = false;
    if (this.creditsPackages().length > 0) {
      this.selectedOption.set(CUSTOM_CREDITS_OPTION);
    }
    this.bookingContext.set({ ...data });
  }

  override open(): void {
    this.closeResultNotified = false;
    super.open();
    if (this.bookingContext().openCheckout) {
      queueMicrotask(() => this.bookSelectedPackage());
    }
  }

  /**
   * Starts checkout for the currently selected credits package.
   * @returns {void}
   */
  bookSelectedPackage(): void {
    if (!this.isUserAdmin() || this.checkoutLoading() || this.showCheckout()) {
      return;
    }
    const selectedPackage = this.selectedPackage();
    if (!selectedPackage) {
      return;
    }
    if (this.bookingConfirmation.invoicePayment()) {
      this.openInvoiceConfirm();
      return;
    }
    this.choiceBusy.set(true);
    this.bookingConfirmation.resolveNeedsPaymentMethodChoice().subscribe({
      next: (needsChoice) => {
        this.choiceBusy.set(false);
        if (needsChoice) {
          this.isPaymentMethodStep.set(true);
          return;
        }
        this.openCheckout();
      },
      error: () => {
        this.choiceBusy.set(false);
        this.openCheckout();
      },
    });
  }

  /**
   * Continues after the first-booking payment-method step.
   * @returns {void}
   */
  confirmPaymentMethod(): void {
    const choice = this.paymentMethodChoice();
    if (!choice || this.choiceBusy() || this.checkoutLoading()) {
      return;
    }
    this.choiceBusy.set(true);
    choice.prepareSelection().subscribe({
      next: (method) => {
        this.choiceBusy.set(false);
        this.isPaymentMethodStep.set(false);
        if (method === FirstBookingPaymentMethod.INVOICE) {
          this.openInvoiceConfirm();
          return;
        }
        this.openCheckout();
      },
      error: () => this.choiceBusy.set(false),
    });
  }

  /**
   * Returns from the payment-method step to package selection.
   * @returns {void}
   */
  backFromPaymentMethod(): void {
    this.isPaymentMethodStep.set(false);
    this.choiceBusy.set(false);
  }

  /**
   * Skips the first-booking auto-renewal prompt after credits were booked.
   * @returns {void}
   */
  skipAutoRenewal(): void {
    this.closeModal(CreditsBookingModalCloseResult.BOOK);
  }

  /**
   * Persists auto-renewal from the first-booking prompt, then closes.
   * @returns {void}
   */
  confirmAutoRenewal(): void {
    const setup = this.autoRenewalSetup();
    if (!setup || this.choiceBusy()) {
      return;
    }
    this.choiceBusy.set(true);
    setup.save().subscribe({
      next: (saved) => {
        this.choiceBusy.set(false);
        if (saved) {
          this.closeModal(CreditsBookingModalCloseResult.BOOK);
        }
      },
      error: () => this.choiceBusy.set(false),
    });
  }

  /**
   * Opens the invoice confirmation for the selected credits package.
   * @returns {void}
   */
  private openInvoiceConfirm(): void {
    const selectedPackage = this.selectedPackage();
    if (!selectedPackage) {
      return;
    }
    this.invoiceConfirmModal.open(
      this.bookingConfirmation.toProductConfirmData(
        selectedPackage,
        null,
        this.selectedQuantity(),
      ),
    );
  }

  /**
   * Continues an invoice-confirmed credits booking.
   * @returns {void}
   */
  onInvoiceBookingConfirmed(): void {
    if (!this.bookingConfirmation.invoicePayment()) {
      this.choiceBusy.set(true);
      this.bookingConfirmation.enableInvoicePayment().subscribe({
        next: () => {
          this.choiceBusy.set(false);
          this.openCheckout();
        },
        error: () => this.choiceBusy.set(false),
      });
      return;
    }
    this.openCheckout();
  }

  /**
   * Returns from Stripe checkout to package selection.
   * @returns {void}
   */
  backToSelection(): void {
    if (
      this.showCheckout() &&
      this.bookingConfirmation.needsPaymentMethodChoice()
    ) {
      this.teardownCheckout();
      this.checkoutLoading.set(false);
      this.showCheckout.set(false);
      this.isPaymentMethodStep.set(true);
      return;
    }
    this.teardownCheckout();
    this.checkoutLoading.set(false);
    this.showCheckout.set(false);
    this.isPaymentMethodStep.set(false);
    this.isAutoRenewalStep.set(false);
  }

  cancel(): void {
    if (
      !this.isConfirmationMode() &&
      this.totalLeftCredits() > 0 &&
      this.bookingContext().amount > 0
    ) {
      this.showConfirmationModal();
      return;
    }

    if (this.isConfirmationMode()) {
      this.confirmationModal.close();
      this.isConfirmationMode.set(false);
    }
    this.closeModal();
  }

  private showConfirmationModal(): void {
    this.bookingContext.update((ctx) => ({
      ...ctx,
      leftCredits: this.totalLeftCredits(),
    }));
    this.isConfirmationMode.set(true);
    this.confirmationModal.open();
  }

  onMainShellClose(): void {
    if (this.closeResultNotified) {
      return;
    }
    this.teardownCheckout();
    if (this.isAutoRenewalStep()) {
      this.bookingConfirmation.markCreditsAutoRenewalPrompted();
      this.emitCloseResult(CreditsBookingModalCloseResult.BOOK);
      return;
    }
    this.emitCloseResult(undefined);
  }

  private teardownCheckout(): void {
    if (this.checkout) {
      this.checkout.unmount();
      this.checkout.destroy();
      this.checkout = undefined;
    }
  }

  private emitCloseResult(
    result: CreditsBookingModalCloseResult | undefined,
  ): void {
    if (this.closeResultNotified) {
      return;
    }
    this.closeResultNotified = true;
    this.modalResult.emit(result);
  }

  private closeModal(result?: CreditsBookingModalCloseResult): void {
    if (result === CreditsBookingModalCloseResult.BOOK) {
      if (!this.bookedSuccessfully) {
        this.bookingConfirmation.showSuccessToast();
        this.bookedSuccessfully = true;
      }
      if (
        !this.isAutoRenewalStep() &&
        this.bookingConfirmation.shouldPromptCreditsAutoRenewal()
      ) {
        this.teardownCheckout();
        this.checkoutLoading.set(false);
        this.showCheckout.set(false);
        this.isPaymentMethodStep.set(false);
        this.isAutoRenewalStep.set(true);
        return;
      }
      if (this.isAutoRenewalStep()) {
        this.bookingConfirmation.markCreditsAutoRenewalPrompted();
      }
    }
    this.teardownCheckout();
    this.emitCloseResult(result);
    super.close();
  }

  /**
   * Creates and mounts the Stripe embedded checkout for the selected package.
   * @returns {void}
   */
  openCheckout(): void {
    const priceId = this.selectedPackage()?.id;
    const quantity = this.selectedQuantity();
    this.checkoutLoading.set(true);
    this.showCheckout.set(true);
    this.stripeAdapter
      .createCreditsBookingCheckoutSession(priceId, quantity)
      .subscribe({
        next: async (session) => {
          try {
            if (session.invoiceBooked || !session.clientSecret) {
              this.closeModal(CreditsBookingModalCloseResult.BOOK);
              return;
            }
            const clientSecret = session.clientSecret;
            this.checkout = await this.stripeService.createCheckout(
              clientSecret,
              async () => {
                try {
                  if (session.checkoutSessionId) {
                    await firstValueFrom(
                      this.stripeAdapter.applyBookedPrice(
                        priceId || '',
                        quantity,
                        session.checkoutSessionId,
                      ),
                    );
                  } else if (priceId) {
                    await firstValueFrom(
                      this.stripeAdapter.applyBookedPrice(priceId, quantity),
                    );
                  }
                  this.bookingConfirmation.rememberPaymentMethodOnFile();
                  this.closeModal(CreditsBookingModalCloseResult.BOOK);
                } catch (applyError) {
                  console.error(
                    '[credits-booking-modal] applyBookedPrice failed',
                    applyError,
                  );
                  this.closeModal();
                }
              },
            );
            this.checkoutLoading.set(false);
            setTimeout(() => {
              this.checkout?.mount('#payment-setup-checkout-element');
            }, 50);
          } catch (error) {
            console.error('[credits-booking-modal] mount failed', error);
            this.checkoutLoading.set(false);
          }
        },
        error: (error) => {
          console.error('[credits-booking-modal] session failed', error);
          this.checkoutLoading.set(false);
          this.showCheckout.set(false);
        },
      });
  }

  continueWithCredits(): void {
    const onContinue = this.bookingContext().onContinueWithCredits;
    if (onContinue) {
      onContinue();
    }
    this.confirmationModal.close();
    this.teardownCheckout();
    this.emitCloseResult(CreditsBookingModalCloseResult.CONTINUE);
    super.close();
  }

  /**
   * Closes the confirmation dialog and returns to package selection.
   * @returns {void}
   */
  backToBooking(): void {
    this.confirmationModal.close();
    this.isConfirmationMode.set(false);
    this.backToSelection();
  }

  /**
   * Loads self-booking credits packages from Stripe and sorts by amount.
   * @returns {void}
   */
  private loadCreditsPackages(): void {
    this.creditsPackagesLoaded.set(false);
    this.stripeAdapter.getCreditsPackages().subscribe({
      next: (prices) => {
        const packages = prices
          .filter((price) => (price.unit_amount ?? 0) > 0)
          .sort((a, b) => this.creditsAmountOf(a) - this.creditsAmountOf(b));
        this.creditsPackages.set(packages);
        if (packages.length > 0 && !this.selectedOption()) {
          this.selectedOption.set(CUSTOM_CREDITS_OPTION);
        }
        this.creditsPackagesLoaded.set(true);
      },
      error: (error) => {
        console.error('[credits-booking-modal] catalog failed', error);
        this.creditsPackages.set([]);
        this.creditsPackagesLoaded.set(true);
      },
    });
  }

  /**
   * Formats a credits number for locale-aware display.
   * @param {number} value Credits amount
   * @returns {string}
   */
  private formatCredits(value: number): string {
    return this.decimalPipe.transform(value, '1.0-0') ?? `${value}`;
  }
}
